// ── GQ Performance Tearsheet ─────────────────────────────────────────
// Institutional QuantStats-style report. Three data sources, in order:
//   1. URL ?run=<uuid> → backtest_runs (run-addressable, primary)
//   2. test_table       → legacy TradingView CSV upload
//   3. generated mock   → so the page always renders something
// PDF via window.print().

// ── Engine-run → legacy trade row adapter ────────────────────────────
// backtest_runs.trades use the engine schema (entryTime/exitTime/pnl/…);
// the tearsheet renderer expects TradingView column names. This adapts.
function adaptRunToTearsheet(run, strategyName) {
  const capital = run.capital || 10000;
  const eng = Array.isArray(run.trades) ? run.trades : [];
  let cumPnL = 0, peak = 0;
  const trades = eng.map((t, i) => {
    cumPnL += (t.pnl || 0);
    if (cumPnL > peak) peak = cumPnL;
    const dd = cumPnL < peak ? cumPnL - peak : 0;
    const dateStr = (t.exitTime ? new Date(t.exitTime) : new Date()).toISOString().slice(0,10);
    return {
      "Date/Time": dateStr,
      "Trade #": i + 1,
      "Type":   t.side === 'short' ? "Short" : "Long",
      "Signal": (t.pnl || 0) >= 0 ? "Buy" : "Sell",
      "Price USD": t.entryPx || 0,
      "Quantity": Math.abs(t.qty || 0).toFixed(4),
      "P&L USD": t.pnl || 0,
      "P&L %": (((t.pnlPct || 0) * 100)).toFixed(2) + "%",
      "Cumulative P&L USD": cumPnL,
      "Cumulative P&L %": (cumPnL / capital * 100).toFixed(2) + "%",
      "Drawdown USD": dd,
      "Drawdown %": (dd / capital * 100).toFixed(2) + "%",
      "Run-up USD": Math.max(0, t.pnl || 0),
      "Run-up %": (Math.max(0, t.pnl || 0) / capital * 100).toFixed(2) + "%",
    };
  });

  // Prefer engine-computed metrics where available; fall back to derived
  const ek = run.metrics || {};
  const pnls    = eng.map(t => t.pnl || 0);
  const wins    = pnls.filter(p => p > 0);
  const losses  = pnls.filter(p => p < 0);
  const totalPnL = pnls.reduce((a,b)=>a+b,0);
  const monthly = {};
  trades.forEach(t => {
    const m = t["Date/Time"].slice(0,7);
    monthly[m] = (monthly[m] || 0) + (parseFloat(t["P&L USD"]) || 0);
  });
  const metrics = {
    totalPnL,
    totalRet:      ek.totalReturn ?? (totalPnL / capital),
    cagr:          ek.cagr ?? 0,
    sharpe:        ek.sharpe ?? 0,
    sortino:       ek.sortino ?? 0,
    calmar:        ek.calmar ?? 0,
    maxDD:         ek.maxDrawdown ?? 0,
    winRate:       ek.winRate ?? (wins.length / Math.max(1, trades.length)),
    profitFactor:  ek.profitFactor ?? 0,
    expectancy:    ek.expectancy ?? 0,
    recovFactor:   ek.maxDrawdown ? (ek.totalReturn || 0) / Math.abs(ek.maxDrawdown) : 0,
    avgWin:        wins.length ? wins.reduce((a,b)=>a+b,0)/wins.length : 0,
    avgLoss:       losses.length ? losses.reduce((a,b)=>a+b,0)/losses.length : 0,
    totalTrades:   trades.length,
    wins:          wins.length,
    losses:        losses.length,
    grossProfit:   wins.reduce((a,b)=>a+b,0),
    grossLoss:     losses.reduce((a,b)=>a+b,0),
    bestTrade:     pnls.length ? Math.max(...pnls) : 0,
    worstTrade:    pnls.length ? Math.min(...pnls) : 0,
    dateStart:     trades[0]?.["Date/Time"] || run.date_start || "—",
    dateEnd:       trades[trades.length-1]?.["Date/Time"] || run.date_end || "—",
    monthly, capital,
  };
  return {
    trades, metrics,
    label: strategyName || (run.asset ? `${run.asset} backtest` : "Backtest run"),
    runId: run.id,
  };
}

// ── Mock data generator ───────────────────────────────────────────────
function generateMockTrades() {
  const rand = window.GQ.utils.seededRng(42);
  const trades = [];
  let cumPnL = 0, peak = 0, tradeNum = 0;
  let dt = new Date("2023-01-03");
  const capital = 10000;
  for (let i = 0; i < 160; i++) {
    dt.setDate(dt.getDate() + Math.floor(rand()*7+1));
    if (dt > new Date("2025-01-01")) break;
    const isWin = rand() < 0.638;
    const pnl = isWin ? (rand()*180+40) : -(rand()*90+20);
    cumPnL += pnl;
    if (cumPnL > peak) peak = cumPnL;
    const dd = cumPnL < peak ? cumPnL - peak : 0;
    tradeNum++;
    trades.push({
      "Date/Time": dt.toISOString().slice(0,10),
      "Trade #": tradeNum,
      "Type": isWin ? "Long" : "Short",
      "Signal": isWin ? "Buy" : "Sell",
      "Price USD": 28000 + rand()*20000,
      "Quantity": (0.1 + rand()*0.4).toFixed(4),
      "P&L USD": pnl,
      "P&L %": (pnl/capital*100).toFixed(2)+"%",
      "Cumulative P&L USD": cumPnL,
      "Cumulative P&L %": (cumPnL/capital*100).toFixed(2)+"%",
      "Drawdown USD": dd,
      "Drawdown %": (dd/capital*100).toFixed(2)+"%",
      "Run-up USD": Math.max(0, pnl),
      "Run-up %": (Math.max(0,pnl)/capital*100).toFixed(2)+"%",
    });
  }
  return trades;
}

// ── Metrics calculation ───────────────────────────────────────────────
function calcMetrics(trades, capital = 10000) {
  if (!trades || trades.length === 0) return null;
  const pnls    = trades.map(t => parseFloat(t["P&L USD"]) || 0);
  const cumPnLs = trades.map(t => parseFloat(t["Cumulative P&L USD"]) || 0);
  const dds     = trades.map(t => parseFloat(t["Drawdown USD"]) || 0);
  const dates   = trades.map(t => new Date(t["Date/Time"]));
  const wins    = pnls.filter(p => p > 0);
  const losses  = pnls.filter(p => p < 0);
  const totalPnL = cumPnLs[cumPnLs.length - 1] || 0;
  const totalRet = totalPnL / capital;
  const daysDiff = (dates[dates.length-1] - dates[0]) / 86400000;
  const years    = daysDiff / 365.25;
  const cagr     = years > 0 ? Math.pow(1 + totalRet, 1/years) - 1 : 0;
  const maxDD    = Math.min(...dds) / capital;
  const calmar   = maxDD !== 0 ? cagr / Math.abs(maxDD) : 0;
  const avgWin   = wins.length   ? wins.reduce((a,b)=>a+b,0)/wins.length : 0;
  const avgLoss  = losses.length ? losses.reduce((a,b)=>a+b,0)/losses.length : 0;
  const profitFactor = losses.reduce((a,b)=>a+Math.abs(b),0) > 0
    ? wins.reduce((a,b)=>a+b,0) / Math.abs(losses.reduce((a,b)=>a+b,0)) : 0;
  // Sharpe from trade P&Ls
  const mean    = pnls.reduce((a,b)=>a+b,0)/pnls.length;
  const std     = Math.sqrt(pnls.reduce((a,r)=>a+Math.pow(r-mean,2),0)/pnls.length);
  const sharpe  = std > 0 ? (mean / std) * Math.sqrt(252/((daysDiff/pnls.length)||1)) : 0;
  const downDev = Math.sqrt(losses.reduce((a,l)=>a+l*l,0)/(losses.length||1));
  const sortino = downDev > 0 ? (mean / downDev) * Math.sqrt(252/((daysDiff/pnls.length)||1)) : 0;
  const expectancy = (wins.length/trades.length)*avgWin + (losses.length/trades.length)*avgLoss;
  const recovFactor = maxDD !== 0 ? totalPnL / Math.abs(maxDD * capital) : 0;
  // Monthly returns
  const monthly = {};
  trades.forEach(t => {
    const m = t["Date/Time"].slice(0,7);
    if (!monthly[m]) monthly[m] = 0;
    monthly[m] += parseFloat(t["P&L USD"]) || 0;
  });
  return {
    totalPnL, totalRet, cagr, maxDD, calmar, sharpe, sortino,
    profitFactor, expectancy, recovFactor, avgWin, avgLoss,
    winRate: wins.length / trades.length,
    totalTrades: trades.length, wins: wins.length, losses: losses.length,
    grossProfit: wins.reduce((a,b)=>a+b,0),
    grossLoss: losses.reduce((a,b)=>a+b,0),
    bestTrade: Math.max(...pnls), worstTrade: Math.min(...pnls),
    dateStart: trades[0]["Date/Time"], dateEnd: trades[trades.length-1]["Date/Time"],
    monthly, capital,
  };
}

// ── SVG equity curve ──────────────────────────────────────────────────
function EquityCurve({ trades, capital = 10000, hideBenchmark = false }) {
  if (!trades || !trades.length) return null;
  const W=600, H=160;
  const equity = [capital, ...trades.map(t => capital + (parseFloat(t["Cumulative P&L USD"])||0))];
  let bench = null;
  if (!hideBenchmark) {
    function benchSeed(seed){let s=seed;return()=>{s=(s*16807)%2147483647;return(s-1)/2147483646;}}
    const br = benchSeed(99); bench = [capital];
    for(let i=1;i<equity.length;i++) bench.push(bench[bench.length-1]*(1+(br()-0.47)*0.03));
  }
  const all = bench ? [...equity, ...bench] : equity;
  const mn=Math.min(...all), mx=Math.max(...all);
  const rx=i=>(i/(equity.length-1))*W;
  const ry=v=>H-((v-mn)/(mx-mn||1))*(H-12)-6;
  const eLine=equity.map((v,i)=>`${i===0?"M":"L"}${rx(i)},${ry(v)}`).join(" ");
  const bLine= bench ? bench.map((v,i)=>`${i===0?"M":"L"}${rx(i)},${ry(v)}`).join(" ") : null;
  const eArea=`${eLine} L${W},${H} L0,${H} Z`;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display:"block" }}>
      <defs>
        <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#11ff99" stopOpacity="0.2"/>
          <stop offset="100%" stopColor="#11ff99" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={eArea} fill="url(#eqGrad)"/>
      {bLine && <path d={bLine} fill="none" stroke="rgba(59,158,255,0.5)" strokeWidth="1" strokeDasharray="4 3"/>}
      <path d={eLine} fill="none" stroke="#11ff99" strokeWidth="1.8" strokeLinecap="round"/>
      {bLine && <text x={W-4} y={10} textAnchor="end" fontSize="9" fill="rgba(59,158,255,0.7)" fontFamily="var(--font-mono)">BTC benchmark</text>}
      <text x={W-4} y={bLine ? 22 : 10} textAnchor="end" fontSize="9" fill="#11ff99" fontFamily="var(--font-mono)">Strategy</text>
    </svg>
  );
}

// ── SVG drawdown chart ────────────────────────────────────────────────
function DrawdownChart({ trades, capital = 10000 }) {
  if (!trades || !trades.length) return null;
  const W=600, H=80;
  const dds = [0, ...trades.map(t => Math.min(0, parseFloat(t["Drawdown USD"])||0) / capital * 100)];
  const mn=Math.min(...dds), mx=0;
  const rx=i=>(i/(dds.length-1))*W;
  const ry=v=>((mx-v)/(mx-mn||1))*(H-4)+2;
  const line=dds.map((v,i)=>`${i===0?"M":"L"}${rx(i)},${ry(v)}`).join(" ");
  const area=`${line} L${W},${H} L0,${H} Z`;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display:"block" }}>
      <defs>
        <linearGradient id="ddGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ff2047" stopOpacity="0.35"/>
          <stop offset="100%" stopColor="#ff2047" stopOpacity="0.05"/>
        </linearGradient>
      </defs>
      <path d={area} fill="url(#ddGrad)"/>
      <path d={line} fill="none" stroke="#ff2047" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1={0} y1={2} x2={W} y2={2} stroke="rgba(255,255,255,0.08)" strokeWidth="0.5"/>
      <text x={4} y={14} fontSize="9" fill="rgba(255,255,255,0.3)" fontFamily="var(--font-mono)">0%</text>
      <text x={4} y={H-4} fontSize="9" fill="rgba(255,32,71,0.6)" fontFamily="var(--font-mono)">{Math.min(...dds).toFixed(1)}%</text>
    </svg>
  );
}

// ── Monthly returns heatmap ───────────────────────────────────────────
const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function MonthlyHeatmap({ monthly, capital = 10000 }) {
  if (!monthly) return null;
  const keys = Object.keys(monthly).sort();
  const years = [...new Set(keys.map(k=>k.slice(0,4)))].sort();
  function color(val) {
    const pct = val / capital * 100;
    if (pct > 6)  return "#11ff99";
    if (pct > 3)  return "rgba(17,255,153,0.7)";
    if (pct > 1)  return "rgba(17,255,153,0.4)";
    if (pct > 0)  return "rgba(17,255,153,0.18)";
    if (pct > -2) return "rgba(255,32,71,0.2)";
    if (pct > -4) return "rgba(255,32,71,0.45)";
    return "#ff2047";
  }
  return (
    <div style={{ overflowX:"auto" }}>
      <table style={{ borderCollapse:"separate", borderSpacing:3, fontFamily:"var(--font-mono)", fontSize:11 }}>
        <thead>
          <tr>
            <th style={{ textAlign:"left", padding:"2px 8px", color:"var(--subtle)", fontWeight:400 }}>Year</th>
            {MONTH_LABELS.map(m => <th key={m} style={{ padding:"2px 6px", color:"var(--subtle)", fontWeight:400, textAlign:"center", width:44 }}>{m}</th>)}
            <th style={{ padding:"2px 8px", color:"var(--subtle)", fontWeight:400 }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {years.map(y => {
            let ytd = 0;
            return (
              <tr key={y}>
                <td style={{ color:"var(--muted)", padding:"2px 8px", fontWeight:500 }}>{y}</td>
                {Array.from({length:12},(_,mi)=>{
                  const key = `${y}-${String(mi+1).padStart(2,"0")}`;
                  const val = monthly[key];
                  if (val !== undefined) ytd += val;
                  const pct = val !== undefined ? (val/capital*100) : null;
                  return (
                    <td key={mi} style={{
                      padding:"4px 2px", textAlign:"center", borderRadius:4,
                      background: val !== undefined ? color(val) : "rgba(255,255,255,0.03)",
                      color: val !== undefined ? (Math.abs(pct)>3?"#000":"var(--ink)") : "var(--subtle)",
                      fontSize:10,
                    }}>
                      {val !== undefined ? (pct >= 0 ? "+" : "")+pct.toFixed(1)+"%" : "—"}
                    </td>
                  );
                })}
                <td style={{ padding:"2px 8px", color: ytd>=0?"var(--green)":"var(--red)", fontWeight:600 }}>
                  {ytd>=0?"+":""}{(ytd/capital*100).toFixed(1)}%
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Metric row ────────────────────────────────────────────────────────
function MetricRow({ label, value, color }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", padding:"7px 0", borderBottom:"1px solid var(--border)" }}>
      <span style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--muted)" }}>{label}</span>
      <span style={{ fontFamily:"var(--font-mono)", fontSize:13, fontWeight:500, color: color||"var(--ink)" }}>{value}</span>
    </div>
  );
}

function fmt(v, isPercent=false, dp=2) {
  if (v === null || v === undefined || isNaN(v)) return "—";
  const n = (v*100).toFixed(dp);
  return isPercent ? (v>=0?"+":"")+n+"%" : (v>=0?"+":"")+n;
}
function fmtMoney(v, dp=0) {
  if (!v && v!==0) return "—";
  return (v>=0?"+$":"-$")+Math.abs(v).toLocaleString("en-US",{minimumFractionDigits:dp,maximumFractionDigits:dp});
}

// ── Main tearsheet ────────────────────────────────────────────────────
function TearsheetPage({ onToast, onNavigate }) {
  const [trades, setTrades]   = React.useState(null);
  const [metrics, setMetrics] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState(null);
  // source: "run" | "supabase" | "mock"
  const [source, setSource]   = React.useState("mock");
  const [strategy, setStrategy] = React.useState("BTC Momentum");
  const [runMeta, setRunMeta] = React.useState(null); // { runId, strategyId } when source === 'run'

  // Re-load when the URL hash changes (so a deep-link to a different run rerenders)
  React.useEffect(() => {
    loadData();
    const onHash = () => loadData();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  async function loadData() {
    setLoading(true); setError(null);

    // 1. URL-addressable run: ?run=<uuid>
    const st = window.GQ?.state;
    const parsed = st?.parseHash ? st.parseHash() : null;
    const runId = parsed?.params?.run;
    if (runId && st?.fetchBacktestRun) {
      try {
        const run = await st.fetchBacktestRun(runId);
        if (run && Array.isArray(run.trades) && run.trades.length > 0) {
          let stratName = null;
          if (run.strategy_id && window.sbClient) {
            try {
              const { data: s } = await window.sbClient
                .from("strategies").select("name").eq("id", run.strategy_id).maybeSingle();
              stratName = s?.name || null;
            } catch (_) {}
          }
          const { trades: ts, metrics: ms, label } = adaptRunToTearsheet(run, stratName);
          setTrades(ts); setMetrics(ms);
          setStrategy(label);
          setRunMeta({ runId: run.id, strategyId: run.strategy_id });
          setSource("run");
          setLoading(false);
          return;
        }
      } catch (e) { /* fall through to legacy paths */ }
    }

    // 2. Legacy test_table (TradingView CSV upload)
    setRunMeta(null);
    try {
      const { data, error: qErr } = await window.sbClient
        .from("test_table")
        .select("*")
        .order("Date/Time", { ascending: true });
      if (qErr) throw qErr;
      if (data && data.length > 0) {
        setTrades(data); setMetrics(calcMetrics(data)); setSource("supabase");
      } else { throw new Error("empty"); }
    } catch (e) {
      // 3. Mock — tearsheet should always render something
      const mock = generateMockTrades();
      setTrades(mock); setMetrics(calcMetrics(mock)); setSource("mock");
    }
    setLoading(false);
  }

  const printReport = () => window.print();

  if (loading) return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <PageHdr icon={FileText} iconColor="var(--primary)" title="Performance tearsheet" subtitle="Loading trade data…"/>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))", gap:10 }}>
        {Array.from({length: 8}).map((_, i) => <SkeletonCard key={i} height={92}/>)}
      </div>
      <div className="card" style={{ minHeight:220 }}>
        <Skeleton width="180px" height={14} style={{ marginBottom: 16 }}/>
        <Skeleton width="100%" height={160} radius={8}/>
      </div>
      <div className="card" style={{ minHeight:120 }}>
        <Skeleton width="180px" height={14} style={{ marginBottom: 16 }}/>
        <Skeleton width="100%" height={80} radius={8}/>
      </div>
    </div>
  );

  const m = metrics;
  if (!m) return (
    <div>
      <PageHdr icon={FileText} iconColor="var(--primary)" title="Performance tearsheet"/>
      <div className="card">
        <EmptyState
          icon={FileText}
          title="No trade data yet."
          body="Run a backtest, connect an exchange, or upload a trades file to generate your first tearsheet."
          primary={{ label: "Run backtest", icon: Play, onClick: () => onToast?.("Opening backtest…") }}
          secondary={{ label: "Upload trades", icon: Upload, onClick: () => onToast?.("Upload UI coming soon…") }}
        />
      </div>
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

      <style>{`
        @media print {
          /* Hide navigation, sidebar, AI launcher, footer */
          .shell > aside,
          .shell > footer,
          .shell .ai-launcher,
          nav, header[role="banner"],
          button[aria-label*="Sign out"],
          .no-print {
            display: none !important;
          }

          /* Reset background to white for printing */
          body, .shell, .main, .card {
            background: white !important;
            color: black !important;
            box-shadow: none !important;
          }

          /* Force charts to print-friendly contrast */
          .card {
            page-break-inside: avoid;
            border: 1px solid #ccc !important;
            margin-bottom: 12px;
          }

          /* Page breaks before each major section */
          .tearsheet-section {
            page-break-before: always;
          }
          .tearsheet-section:first-of-type {
            page-break-before: auto;
          }

          /* Force text colors to print-friendly */
          .text-muted, .subtle, [style*="color: var(--muted)"] {
            color: #555 !important;
          }
          [style*="color: var(--ink)"] {
            color: black !important;
          }

          /* Show the watermark only on print */
          #print-watermark {
            display: block !important;
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-30deg);
            font-size: 96px;
            color: rgba(0,0,0,0.04);
            font-family: 'Inter Tight', sans-serif;
            font-weight: 700;
            letter-spacing: 0.1em;
            pointer-events: none;
            z-index: 0;
          }

          /* Charts: force minimum heights so they don't crush */
          .js-plotly-plot {
            min-height: 250px;
            max-width: 100% !important;
          }

          /* Tables shouldn't break mid-row */
          tr {
            page-break-inside: avoid;
          }
        }
      `}</style>

      {/* Print-only letterhead header (hidden on screen) */}
      <div id="print-header" style={{ display:"none" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18, paddingBottom:12, borderBottom:"2px solid #000" }}>
          <div style={{ display:"flex", gap:14, alignItems:"center" }}>
            <svg width="48" height="48" viewBox="0 0 56 56" fill="none">
              <g fill="rgba(17,255,153,.28)"><circle cx="8" cy="8" r="1.5"/><circle cx="18" cy="8" r="1.5"/><circle cx="28" cy="8" r="1.5"/><circle cx="38" cy="8" r="1.5"/><circle cx="48" cy="8" r="1.5"/><circle cx="8" cy="18" r="1.5"/><circle cx="48" cy="18" r="1.5"/><circle cx="8" cy="28" r="1.5"/><circle cx="48" cy="28" r="1.5"/><circle cx="8" cy="38" r="1.5"/><circle cx="48" cy="38" r="1.5"/><circle cx="8" cy="48" r="1.5"/><circle cx="18" cy="48" r="1.5"/><circle cx="28" cy="48" r="1.5"/><circle cx="38" cy="48" r="1.5"/><circle cx="48" cy="48" r="1.5"/></g>
              <g fill="rgba(17,255,153,.55)"><circle cx="18" cy="18" r="1.8"/><circle cx="28" cy="18" r="1.8"/><circle cx="38" cy="18" r="1.8"/><circle cx="18" cy="28" r="1.8"/><circle cx="38" cy="28" r="1.8"/><circle cx="18" cy="38" r="1.8"/><circle cx="28" cy="38" r="1.8"/><circle cx="38" cy="38" r="1.8"/></g>
              <circle cx="28" cy="28" r="4" fill="#11ff99"/>
            </svg>
            <div>
              <div style={{ fontFamily:"Inter, sans-serif", fontSize:24, fontWeight:600, color:"#000", letterSpacing:"-0.018em" }}>Genetic Quant<span style={{ color:"#666" }}>.ai™</span></div>
              <div style={{ fontFamily:"Inter, sans-serif", fontSize:10, color:"#666", marginTop:2, letterSpacing:".06em", textTransform:"uppercase" }}>Institutional Performance Report · Confidential</div>
            </div>
          </div>
          <div style={{ textAlign:"right", fontFamily:"Inter, sans-serif", fontSize:10, color:"#666" }}>
            <div style={{ marginBottom:3 }}>Generated {new Date().toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}</div>
            <div style={{ marginBottom:3 }}>Strategy: <span style={{ color:"#000", fontWeight:500 }}>{strategy}</span></div>
            <div style={{ marginBottom:3 }}>{m.dateStart} — {m.dateEnd}</div>
            <div style={{ fontFamily:"JetBrains Mono, monospace", letterSpacing:".04em" }}>Report ID: GQ-{runMeta?.runId ? runMeta.runId.slice(0,8).toUpperCase() : String(new Date().getFullYear()) + String(strategy.length * 7919 % 900000 + 100000)}</div>
          </div>
        </div>
      </div>

      {/* Print-only watermark (hidden on screen) */}
      <div id="print-watermark" style={{ display:"none" }}>GENETICQUANT</div>

      {/* Print-only footer (hidden on screen) */}
      <div id="print-footer" style={{ display:"none" }}>
        <span>This material is intended for educational and informational purposes only. None of the information should be considered financial advice. Past performance is not necessarily an accurate predictor of future results. © {new Date().getFullYear()} Quant Market Intelligence.</span>
      </div>
      {/* Print-only sections rendered above. Replaced. */}

      {/* Screen header */}
      <PageHdr
        icon={FileText} iconColor="var(--primary)"
        title="Performance tearsheet"
        subtitle={
          source === "run"
            ? `${strategy} · ${m.dateStart} → ${m.dateEnd} · Live backtest run · ${runMeta?.runId?.slice(0,8)}`
            : `${strategy} · ${m.dateStart} → ${m.dateEnd} · ${source === "supabase" ? "Live data" : "Sample data — connect an exchange or upload trades"}`
        }
        action={
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            {source === "run"  && <span className="badge badge-green"  style={{ fontSize:10 }}>Live run</span>}
            {source === "mock" && <span className="badge badge-muted" style={{ fontSize:10 }}>Sample data</span>}
            {source === "run" && runMeta?.strategyId && (
              <button
                onClick={() => window.navigateTo("strategy-detail", { id: runMeta.strategyId })}
                className="btn btn-secondary btn-sm no-print"
              >
                Open strategy
              </button>
            )}
            {source !== "run" && (
              <select value={strategy} onChange={e=>setStrategy(e.target.value)} style={{ background:"var(--bg-card)", border:"1px solid var(--border-strong)", borderRadius:"var(--r-md)", padding:"6px 10px", fontFamily:"var(--font-ui)", fontSize:12, color:"var(--ink)", outline:"none" }}>
                <option>BTC Momentum</option>
                <option>ETH Alpha</option>
                <option>SOL Breakout</option>
              </select>
            )}
            <button onClick={printReport} className="btn btn-primary btn-sm no-print">
              <Printer size={12}/> Export PDF
            </button>
          </div>
        }
      />

      {source === "mock" && (
        <div className="no-print" style={{
          background: "rgba(251,191,36,0.08)",
          border: "1px solid rgba(251,191,36,0.3)",
          borderRadius: "var(--r-md)",
          padding: "12px 16px",
          display: "flex", alignItems: "flex-start", gap: 10,
          fontFamily: "var(--font-ui)", fontSize: 13, color: "rgba(251,191,36,0.95)",
          lineHeight: 1.5,
        }}>
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }}/>
          <div>
            <b>Demo data — these numbers are illustrative.</b> Run a backtest from Strategy Lab to see metrics for your own strategy, or upload a TradingView CSV via Settings → Data.
          </div>
        </div>
      )}

      {/* KPI strip */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))", gap:10 }}>
        <StatCard title="Net return"    value={fmt(m.totalRet,true)}       accent="green"  icon={TrendingUp}/>
        <StatCard title="CAGR"          value={fmt(m.cagr,true)}           accent="green"  icon={ArrowUp}/>
        <StatCard title="Sharpe ratio"  value={m.sharpe.toFixed(2)}        accent="purple" icon={Activity}/>
        <StatCard title="Sortino"       value={m.sortino.toFixed(2)}       accent="purple" icon={Brain}/>
        <StatCard title="Max drawdown"  value={fmt(m.maxDD,true)}          accent="red"    icon={TrendingDown}/>
        <StatCard title="Calmar"        value={m.calmar.toFixed(2)}        accent="cyan"   icon={Target}/>
        <StatCard title="Win rate"      value={(m.winRate*100).toFixed(1)+"%" } accent="green" icon={Check}/>
        <StatCard title="Profit factor" value={m.profitFactor.toFixed(2)}  accent="cyan"   icon={DollarSign}/>
      </div>

      {/* Equity curve */}
      <div className="card">
        <div className="card-title" style={{ display:"flex", justifyContent:"space-between" }}>
          <span>Equity curve</span>
          <span style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--muted)", display:"flex", gap:14 }}>
            <span style={{ color:"#11ff99" }}>— Strategy</span>
            {source !== "run" && <span style={{ color:"rgba(59,158,255,0.7)" }}>--- BTC benchmark</span>}
          </span>
        </div>
        <div style={{ borderRadius:8, overflow:"hidden", background:"var(--bg-deep)" }}>
          <EquityCurve trades={trades} capital={m.capital} hideBenchmark={source === "run"}/>
        </div>
        <div style={{ display:"flex", gap:20, marginTop:12 }}>
          {[
            {l:"Net P&L",    v:fmtMoney(m.totalPnL), c:"var(--green)"},
            {l:"Best trade", v:fmtMoney(m.bestTrade,2), c:"var(--green)"},
            {l:"Worst trade",v:fmtMoney(m.worstTrade,2),c:"var(--red)"},
            {l:"Avg win",    v:fmtMoney(m.avgWin,2),  c:"var(--green)"},
            {l:"Avg loss",   v:fmtMoney(m.avgLoss,2), c:"var(--red)"},
            {l:"Expectancy", v:fmtMoney(m.expectancy,2)},
          ].map(x=>(
            <div key={x.l}>
              <div style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)" }}>{x.l}</div>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:13, color:x.c||"var(--ink)" }}>{x.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Drawdown */}
      <div className="card">
        <div className="card-title">Underwater / drawdown</div>
        <div style={{ borderRadius:8, overflow:"hidden", background:"var(--bg-deep)" }}>
          <DrawdownChart trades={trades} capital={m.capital}/>
        </div>
        <div style={{ display:"flex", gap:20, marginTop:10 }}>
          {[
            {l:"Max drawdown",   v:fmt(m.maxDD,true),      c:"var(--red)"},
            {l:"Recovery factor",v:m.recovFactor.toFixed(2)},
            {l:"Calmar ratio",   v:m.calmar.toFixed(2)},
            {l:"Total trades",   v:m.totalTrades},
            {l:"Wins",           v:m.wins,  c:"var(--green)"},
            {l:"Losses",         v:m.losses,c:"var(--red)"},
          ].map(x=>(
            <div key={x.l}>
              <div style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)" }}>{x.l}</div>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:13, color:x.c||"var(--ink)" }}>{x.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly heatmap + metrics */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:12 }}>
        <div className="card">
          <div className="card-title">Monthly returns</div>
          <MonthlyHeatmap monthly={m.monthly} capital={m.capital}/>
        </div>
        <div className="card" style={{ minWidth:220 }}>
          <div className="card-title">Full statistics</div>
          <MetricRow label="Total return"   value={fmt(m.totalRet,true)}         color={m.totalRet>=0?"var(--green)":"var(--red)"}/>
          <MetricRow label="CAGR"           value={fmt(m.cagr,true)}             color={m.cagr>=0?"var(--green)":"var(--red)"}/>
          <MetricRow label="Sharpe ratio"   value={m.sharpe.toFixed(3)}          color="var(--primary)"/>
          <MetricRow label="Sortino ratio"  value={m.sortino.toFixed(3)}         color="var(--primary)"/>
          <MetricRow label="Calmar ratio"   value={m.calmar.toFixed(3)}          color="var(--accent)"/>
          <MetricRow label="Max drawdown"   value={fmt(m.maxDD,true)}            color="var(--red)"/>
          <MetricRow label="Profit factor"  value={m.profitFactor.toFixed(3)}    color="var(--accent)"/>
          <MetricRow label="Win rate"       value={(m.winRate*100).toFixed(2)+"%"} color="var(--green)"/>
          <MetricRow label="Total trades"   value={m.totalTrades}/>
          <MetricRow label="Gross profit"   value={fmtMoney(m.grossProfit)}      color="var(--green)"/>
          <MetricRow label="Gross loss"     value={fmtMoney(m.grossLoss)}        color="var(--red)"/>
          <MetricRow label="Expectancy"     value={fmtMoney(m.expectancy,2)}     color={m.expectancy>=0?"var(--green)":"var(--red)"}/>
          <MetricRow label="Recovery factor" value={m.recovFactor.toFixed(2)}    color="var(--accent)"/>
        </div>
      </div>

      {/* Trade log */}
      <div className="card">
        <div className="card-title" style={{ display:"flex", justifyContent:"space-between" }}>
          <span>Trade log ({m.totalTrades} trades)</span>
          <span style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--subtle)" }}>Most recent 20 shown</span>
        </div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ borderBottom:"1px solid var(--border)" }}>
                {["#","Date","Type","Signal","Price","Qty","P&L","Cumulative","Drawdown"].map(h=>(
                  <th key={h} style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)", fontWeight:500, textAlign:"left", padding:"4px 8px", paddingBottom:8 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...trades].reverse().slice(0,20).map((t,i)=>{
                const pnl = parseFloat(t["P&L USD"])||0;
                return (
                  <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                    <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--subtle)", padding:"7px 8px" }}>{t["Trade #"]}</td>
                    <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--muted)",  padding:"7px 8px" }}>{t["Date/Time"]?.slice(0,10)}</td>
                    <td style={{ fontFamily:"var(--font-ui)",  fontSize:11, color:"var(--ink)",    padding:"7px 8px" }}>{t["Type"]}</td>
                    <td style={{ padding:"7px 8px" }}><span className={`badge ${t["Signal"]==="Buy"?"badge-green":"badge-red"}`} style={{ fontSize:10 }}>{t["Signal"]}</span></td>
                    <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--muted)", padding:"7px 8px" }}>${parseFloat(t["Price USD"]).toFixed(0)}</td>
                    <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--muted)", padding:"7px 8px" }}>{t["Quantity"]}</td>
                    <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color:pnl>=0?"var(--green)":"var(--red)", padding:"7px 8px", fontWeight:500 }}>{pnl>=0?"+":""}{pnl.toFixed(2)}</td>
                    <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--muted)", padding:"7px 8px" }}>{t["Cumulative P&L %"]}</td>
                    <td style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--red)",   padding:"7px 8px" }}>{t["Drawdown %"]}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Disclaimer */}
      <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--subtle)", lineHeight:1.6, padding:"12px 16px", background:"rgba(255,255,255,0.02)", borderRadius:"var(--r-md)", border:"1px solid var(--border)" }}>
        This material is intended for educational and informational purposes only. None of the information should be considered financial advice. All performance figures quoted are exclusive of fees, trading commissions, and slippage. Past performance is not necessarily an accurate predictor of future results.
      </div>

      <style>{`
        @keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @media print {
          @page { size: A4 portrait; margin: 18mm 16mm 22mm 16mm; }
          body { background:#fff!important; color:#000!important; font-size:10pt; }
          .no-print { display:none!important; }
          #print-header { display:block!important; }
          #print-watermark {
            display:block!important;
            position:fixed; top:50%; left:50%;
            transform:translate(-50%,-50%) rotate(-32deg);
            font-family:'Inter Tight','Inter',sans-serif;
            font-size:120pt; font-weight:700;
            color:rgba(17,255,153,0.04);
            z-index:0; pointer-events:none; letter-spacing:-0.04em;
          }
          #print-footer {
            display:block!important;
            position:fixed; bottom:8mm; left:16mm; right:16mm;
            font-family:'Inter',sans-serif; font-size:8pt; color:#666;
            text-align:center; line-height:1.4;
            border-top:1px solid #ddd; padding-top:6mm;
          }
          .card { background:#fff!important; border:1px solid #ddd!important; border-radius:4px!important; break-inside:avoid; margin-bottom:10pt; box-shadow:none!important; }
          .card-title { color:#000!important; font-size:10pt!important; }
          * { color:#000!important; }
          .badge-green { background:#dcfce7!important; color:#166534!important; }
          .badge-red   { background:#fee2e2!important; color:#991b1b!important; }
          h1, h2, h3 { color:#000!important; }
        }
      `}</style>
    </div>
  );
}

window.TearsheetPage = TearsheetPage;
